import { Injectable } from '@angular/core';
import { Router, Resolve, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, EMPTY } from 'rxjs';
import { catchError, finalize, map, skip } from 'rxjs/operators';

// Services
import { LoadingIndicatorService } from '@app/services/loading-indicator.service';
import { GraphQLErrorUtilsService } from '@app/services/utils/graphql-error-utils.service';
import { MyProfileService } from '@app/services/my-profile.service';
import { AuthService } from '@app/auth/auth.service';
import { FranchiseLocation } from '@app/models/franchise.model';

@Injectable()
export class PreferredLocationResolver implements Resolve<any> {
  // Routes that needs to perform a "non-CCP" check.
  ROUTE_PATH_WITH_NON_CCP_CHECKING = ['payments/add', 'payments/:id'];

  constructor(
    private router: Router,
    private myProfileService: MyProfileService,
    private authService: AuthService,
    private errorUtils: GraphQLErrorUtilsService,
    private loadingIndicatorService: LoadingIndicatorService
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    this.loadingIndicatorService.show();

    // Fetch the "preferred location" data
    this.authService
      .fetchAccountId()
      .subscribe(() => this.myProfileService.fetchLocationList());
    // Handle Returns
    return this.myProfileService.preferredLocation$.pipe(
      skip(1),
      finalize(() => this.loadingIndicatorService.hide()),
      map((preferredLocation) => {
        // Take several checks.
        if (this.needCcpCheck(route)) {
          this.doCcpChecking(route, preferredLocation);
        }

        // Normal return
        return preferredLocation;
      }),
      catchError((error) => {
        console.log(
          'PreferredLocationResolver ERROR:',
          this.errorUtils.parseGraphQLError(error).outputMessage
        );

        // Back route to the "root".
        this.router.navigate(['/']);

        return EMPTY;
      })
    );
  }

  // #region -> Checks <-
  // ----------

  doCcpChecking(
    route: ActivatedRouteSnapshot,
    preferredLocation: FranchiseLocation
  ) {
    // Check if the preferred location has the limitation to proceed.
    const isCcpEnabledInPreferredLocation =
      this.myProfileService.isCcpEnabled(preferredLocation);

    if (!isCcpEnabledInPreferredLocation) {
      // CCP not enabled, treat it as an error.
      throw this.errorUtils.generateGraphQLError(
        'Non-CCP Access Deny',
        '401',
        'Could not access to this page due to a non-CCP preferred location.',
        [
          route.pathFromRoot
            .map((item) =>
              item.url.map((segment) => segment.toString()).join('/')
            )
            .join('/'),
        ]
      );
    }
  }

  // ----------
  // #endregion

  // ///////////////////////////

  private needCcpCheck(route: ActivatedRouteSnapshot): boolean {
    // Check if the "route path" is in the list.
    const routePath = route.routeConfig.path;
    if (routePath) {
      return this.ROUTE_PATH_WITH_NON_CCP_CHECKING.includes(routePath);
    }
    return false;
  }
}
